import { CompanyRoleEnumLabels } from "@boundless/bl-api-client/customer/models/CompanyRoleEnumLabels"
import { MembershipActivityActionEnum } from "@boundless/bl-api-client/customer/models/MembershipActivityActionEnum"
import { MembershipActivityActorEnum } from "@boundless/bl-api-client/customer/models/MembershipActivityActorEnum"

import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatDateTime } from "@/lib/utils"

import type { MembershipActivity } from "@boundless/bl-api-client/customer/models"

/**
 * Who caused the change, as the borrower reads it.
 *
 * Staff are one label rather than a name: which employee made the change is
 * not the borrower's business, and the backend never sends their address.
 */
function actorLabel(activity: MembershipActivity): string {
  switch (activity.actorType) {
    case MembershipActivityActorEnum.staff:
      return "Boundless"
    case MembershipActivityActorEnum.system:
      return "Automatic"
    default:
      return activity.actorEmail ?? ""
  }
}

/** Role name shared with the members table, so the two cannot disagree. */
function roleLabel(role: string | null | undefined): string {
  if (!role) {
    return ""
  }
  return CompanyRoleEnumLabels[role as keyof typeof CompanyRoleEnumLabels]
}

/**
 * The sentence for one recorded change.
 *
 * Written here rather than sent by the backend, the same split the staff
 * Customer history uses: the API carries structured fields, the client
 * carries the copy.
 */
function actionSentence(activity: MembershipActivity): string {
  const { targetEmail, fromRole, toRole } = activity

  switch (activity.action) {
    case MembershipActivityActionEnum.invited:
      return `Invited ${targetEmail} as ${roleLabel(toRole)}`
    case MembershipActivityActionEnum.invitation_cancelled:
      return `Cancelled the invitation for ${targetEmail}`
    case MembershipActivityActionEnum.invitation_expired:
      return `Invitation for ${targetEmail} expired`
    case MembershipActivityActionEnum.invitation_accepted:
      return `Accepted the invitation and joined as ${roleLabel(toRole)}`
    case MembershipActivityActionEnum.joined:
      return `${targetEmail} joined as ${roleLabel(toRole)}`
    case MembershipActivityActionEnum.role_changed:
      return `Changed ${targetEmail} from ${roleLabel(fromRole)} to ${roleLabel(toRole)}`
    case MembershipActivityActionEnum.removed:
      return `Removed ${targetEmail} (${roleLabel(fromRole)})`
  }

  // An action this bundle predates. Better a blank line than the wrong verb.
  return ""
}

function MembershipActivityTableStructure({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-56">Date</TableHead>
          <TableHead className="hidden md:table-cell">Actor</TableHead>
          <TableHead>Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

interface Props {
  activities: MembershipActivity[]
}

/** A company's recorded membership changes, newest first. */
export function MembershipActivityTable({ activities }: Props) {
  if (activities.length === 0) {
    return (
      <p className="text-muted-foreground py-6 text-center text-sm">
        No membership changes recorded yet.
      </p>
    )
  }

  return (
    <MembershipActivityTableStructure>
      {activities.map((activity) => (
        <TableRow key={activity.uuid} className="h-10">
          <TableCell className="whitespace-nowrap">
            {formatDateTime(activity.createdAt)}
          </TableCell>
          <TableCell className="hidden md:table-cell">
            {actorLabel(activity)}
          </TableCell>
          <TableCell>
            <div>{actionSentence(activity)}</div>
            <div className="text-muted-foreground max-w-56 truncate text-xs md:hidden">
              {actorLabel(activity)}
            </div>
          </TableCell>
        </TableRow>
      ))}
    </MembershipActivityTableStructure>
  )
}

export function FallbackMembershipActivityTable() {
  return (
    <MembershipActivityTableStructure>
      {Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell className="w-56">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="hidden md:table-cell">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-6" />
          </TableCell>
        </TableRow>
      ))}
    </MembershipActivityTableStructure>
  )
}
